import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { ArrowRight, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { formatApiError } from '@/lib/formatApiError';
import { formatCount, formatCtr, formatDuration, formatWatch, rangeQuery } from '@/lib/channel';
import { SeoScoreRing } from '@/components/channel/SeoScoreRing';
import { ChannelSectionNav } from '@/components/channel/ChannelSectionNav';
import { VideoThumb } from '@/components/channel/VideoThumb';
import { ChannelTrendsNav } from '@/components/channel/ChannelTrendsNav';
import { RangeSwitcher } from '@/components/channel/RangeSwitcher';
import { disconnectYouTube, subscribeYouTubeAccount } from '@/lib/youtubeConnection';

let lastOauthSearch = '';

async function beginYouTubeConnect(returnPath) {
  const { data } = await api.post('/social-publish/youtube/oauth/begin', {
    return_path: returnPath,
  });
  if (data?.authorization_url) {
    window.location.href = data.authorization_url;
    return;
  }
  throw new Error('Could not start YouTube login');
}

function consumeYouTubeOAuth(reload) {
  const search = window.location.search;
  if (!search.includes('youtube=') || search === lastOauthSearch) return;
  lastOauthSearch = search;
  const params = new URLSearchParams(search);
  const youtube = params.get('youtube');
  if (youtube === 'connected') toast.success('YouTube connected.');
  else if (params.get('message') === 'missing_scopes') {
    toast.error('Google did not grant analytics access. Approve every permission on the consent screen, then reconnect.');
  } else if (youtube === 'error') {
    toast.error('YouTube reconnect did not finish. Try Reconnect YouTube again.');
  } else return;
  params.delete('youtube');
  params.delete('message');
  const next = params.toString();
  const path = window.location.pathname || '/dashboard';
  window.history.replaceState({}, '', next ? `${path}?${next}` : path);
  reload();
}

export function useChannelOverview(preset = '28d', start = '', end = '', returnPath = '/dashboard', contentFormat = 'all') {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [connectBusy, setConnectBusy] = useState(false);
  const [disconnectBusy, setDisconnectBusy] = useState(false);
  const loadSeq = useRef(0);

  const load = useCallback(async () => {
    if (preset === 'custom' && (!start || !end)) {
      setLoading(false);
      return;
    }
    const seq = ++loadSeq.current;
    try {
      const { data: payload } = await api.get('/channel/overview', {
        params: { ...rangeQuery(preset, start, end), format: contentFormat || 'all' },
      });
      if (seq !== loadSeq.current) return;
      setData(payload);
    } catch (err) {
      if (seq !== loadSeq.current) return;
      toast.error(formatApiError(err, 'Could not load channel performance'));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }, [preset, start, end, contentFormat]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    if (window.location.search.includes('youtube=connected')) {
      loadSeq.current += 1;
      setData({
        connected: true,
        scopes_ok: false,
        channel_title: null,
        channel_id: null,
        recommendations: [],
        videos: [],
        best_videos: [],
        weakest_videos: [],
        studio_home: null,
        lifetime: { views: 0, subscribers: 0, videos: 0 },
        totals: {},
        series: [],
        syncing: true,
      });
    }
    consumeYouTubeOAuth(load);
  }, [load]);

  useEffect(() => subscribeYouTubeAccount(() => {
    loadSeq.current += 1;
    setData({
      connected: false,
      scopes_ok: false,
      channel_title: null,
      channel_id: null,
      recommendations: [],
      videos: [],
      best_videos: [],
      weakest_videos: [],
      studio_home: null,
      lifetime: { views: 0, subscribers: 0, videos: 0 },
      totals: {},
      syncing: false,
    });
    load();
  }), [load]);

  useEffect(() => {
    if (!data?.syncing) return undefined;
    const timer = setTimeout(load, 4000);
    return () => clearTimeout(timer);
  }, [data?.syncing, load]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post('/channel/sync');
      await load();
    } catch (err) {
      toast.error(formatApiError(err, 'Could not refresh YouTube'));
    } finally {
      setRefreshing(false);
    }
  };

  const connect = async () => {
    setConnectBusy(true);
    try {
      await beginYouTubeConnect(returnPath);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not connect YouTube'));
      setConnectBusy(false);
    }
  };

  const disconnect = async () => {
    setDisconnectBusy(true);
    try {
      await disconnectYouTube();
      toast.success('YouTube disconnected');
    } catch (err) {
      toast.error(formatApiError(err, 'Could not disconnect YouTube'));
    } finally {
      setDisconnectBusy(false);
    }
  };

  return { data, loading, refreshing, connectBusy, disconnectBusy, refresh, connect, disconnect, load };
}

function ideaVideo(brief, videos) {
  const ids = new Set((brief?.evidence_video_ids || []).filter(Boolean));
  const topic = String(brief?.topic || '').toLowerCase();
  return (
    videos.find((video) => ids.has(video.video_id))
    || videos.find((video) => topic && String(video.title || '').toLowerCase().includes(topic))
    || null
  );
}

export function WhatToMakeNext({ data, loading }) {
  const navigate = useNavigate();
  const briefs = data?.connected
    ? (Array.isArray(data?.recommendations) ? data.recommendations : []).slice(0, 10)
    : [];
  const videos = Array.isArray(data?.videos) ? data.videos : [];

  return (
    <section id="what-to-make-next" className="group w-full h-[240px] sm:h-[320px] lg:h-[440px] flex flex-col overflow-hidden rounded-[18px] bg-black/35 p-3 space-y-2 transition-colors hover:bg-white/[0.08]">
      <div>
        <h2 className="text-sm font-semibold text-[#d5dbe6] tracking-tight transition-all group-hover:text-white group-hover:font-extrabold">10 new ideas for your next video</h2>
        <p className="text-[11px] text-[#7d8799] mt-0.5">
          {briefs.length > 0
            ? `${briefs.length} new ideas from competitor analysis and from videos you already posted.`
            : 'New ideas from competitor analysis and from videos you already posted.'}
        </p>
      </div>
      {loading && !data ? (
        <div className="h-16 flex items-center text-xs text-[#7d8799]">Looking at your channel…</div>
      ) : null}
      {!loading && data && !data.connected ? (
        <p className="text-xs text-[#7d8799]">Connect YouTube to get ideas from your channel.</p>
      ) : null}
      {!loading && data?.connected && briefs.length === 0 ? (
        <p className="text-xs text-[#7d8799]">
          Ideas show up after your posted videos and competitor channels sync.
        </p>
      ) : null}
      {briefs.length > 0 ? (
        <ul className="flex-1 min-h-0 overflow-y-auto space-y-0.5 pr-1">
          {briefs.map((brief, index) => {
            const sample = ideaVideo(brief, videos);
            const ideaId = brief.id || `${index}`;
            return (
              <li key={ideaId}>
                <button
                  type="button"
                  onClick={() => navigate(`/channel/next/${encodeURIComponent(ideaId)}`)}
                  className="w-full flex items-center gap-3 rounded-2xl px-2 py-2 text-left hover:bg-white/[0.04]"
                >
                  <VideoThumb video={sample || { video_id: (brief.evidence_video_ids || [])[0] }} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-white truncate">{brief.working_title}</span>
                    <span className="block text-[11px] text-[#8b95a7] truncate">
                      {brief.why_code === 'competitor_gap' || brief.why_code === 'competitor_outlier' ? 'Competitor analysis · ' : ''}
                      {brief.why_code === 'beats_median' || brief.why_code === 'channel_video' ? 'Your videos · ' : ''}
                      {brief.why_code === 'ai_idea' && (brief.evidence_video_ids || []).length ? 'Your videos · ' : ''}
                      {brief.why_code === 'peer_strategy' || brief.why_code === 'keyword_gap' ? 'Channels like yours · ' : ''}
                      {brief.format === 'short' ? 'Short' : 'Video'}
                      {brief.weekday ? ` · ${brief.weekday}` : ''}
                      {(brief.why || brief.hook) ? ` · ${brief.why || brief.hook}` : ''}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}
    </section>
  );
}

function highlightFigures(data, { allowLifetimeFallback = false, contentFormat = 'all' } = {}) {
  const totals = data?.totals || {};
  const lifetime = data?.lifetime || {};
  const rangeViews = Number(totals.views || 0);
  const lifetimeViews = Number(lifetime.views || 0);
  const lifetimeSubs = Number(lifetime.subscribers || data?.subscriber_count || 0);
  const useLifetime = allowLifetimeFallback
    && contentFormat === 'all'
    && rangeViews <= 0
    && (lifetimeViews > 0 || lifetimeSubs > 0);
  return {
    basis: useLifetime ? 'lifetime' : 'range',
    views: useLifetime ? lifetimeViews : rangeViews,
    subscribers: contentFormat === 'all'
      ? (useLifetime ? lifetimeSubs : Number(totals.net_subscribers || 0))
      : null,
    watch: useLifetime ? null : totals.watch_minutes,
    ctr: useLifetime ? null : totals.ctr,
  };
}

const CONTENT_FORMATS = [
  ['shorts', 'YT Shorts'],
  ['videos', 'YouTube videos'],
  ['all', 'Both'],
];

function ContentFormatSwitcher({ value, onChange, spaceBeforeLast = false }) {
  return (
    <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Video type">
      {CONTENT_FORMATS.map(([id, label]) => {
        const active = value === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              spaceBeforeLast && id === 'all' ? 'ml-3' : ''
            } ${
              active
                ? 'bg-white text-[#10141c]'
                : 'text-[#7d8799] bg-[#0c1018] border border-[#1f2635] hover:text-white'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function formatViewLabel(contentFormat) {
  if (contentFormat === 'shorts') return 'Shorts views';
  if (contentFormat === 'videos') return 'YouTube video views';
  return 'Views';
}

function rangeCaption(preset, start, end) {
  if (preset === '24h') return 'last 24 hours';
  if (preset === '7d') return 'last 7 days';
  if (preset === '28d') return 'last 28 days';
  if (preset === '90d') return 'last 90 days';
  if (preset === '12m') return 'last 12 months';
  if (preset === 'custom' && start && end) return `${start} to ${end}`;
  return 'this range';
}

function signedCount(value) {
  const n = Number(value) || 0;
  return `${n > 0 ? '+' : ''}${formatCount(n)}`;
}

function studioViewCount(data) {
  const value = Number(data?.studio_views);
  return Number.isFinite(value) ? value : 0;
}

function subscriberChange(value) {
  if (value == null) return '';
  const n = Number(value);
  const prefix = n > 0 ? '+' : '';
  return `${prefix}${formatCount(n)} in last 28 days`;
}

export function YouTubeHighlights({ data, loading, connect, connectBusy, contentFormat = 'all', onContentFormat }) {
  const figures = highlightFigures(data, { allowLifetimeFallback: true, contentFormat });
  const showMetrics = Boolean(data?.connected && data?.scopes_ok);
  const top = (data?.best_videos || [])[0];
  const latest = data?.studio_home?.latest_short;
  const allTimeSubs = data?.studio_home?.subscribers ?? data?.lifetime?.subscribers;
  const gained = data?.studio_home?.subscribers_gained_28d;
  const stats = [
    ['Views', 'All time', showMetrics ? formatCount(studioViewCount(data)) : '—'],
    ['Watch', 'Last 28 days', showMetrics && figures.watch != null ? formatWatch(figures.watch) : '—'],
    ['Clicks', 'Last 28 days', showMetrics && figures.ctr != null ? formatCtr(figures.ctr) : '—'],
    ['Fans', 'All time', showMetrics && allTimeSubs != null ? formatCount(allTimeSubs) : '—'],
  ];

  const videoCard = (video, eyebrow, detail, href) => (
    <Link to={href} className="flex items-center gap-3 rounded-xl border-0 bg-white/[0.05] px-3 py-3 min-w-0 lg:min-w-[240px] lg:max-w-[320px] hover:bg-white/[0.09]">
      <VideoThumb video={video} className="h-16 w-28 rounded-lg" />
      <span className="min-w-0 flex-1">
        <span className="block text-[10px] uppercase tracking-wide text-[#7d8799]">{eyebrow}</span>
        <span className="block text-sm font-medium text-white line-clamp-2 mt-1">{video.title || video.video_id}</span>
        <span className="block text-xs text-[#8b95a7] mt-1">{detail}</span>
      </span>
    </Link>
  );

  return (
    <section id="youtube-highlights" className="group rounded-[18px] border-0 bg-gradient-to-br from-[#2a4570] via-[#1c2a40] to-[#141a24] px-5 py-5 sm:px-6 sm:py-6 space-y-5 font-jakarta transition-colors duration-200 hover:from-[#345484] hover:via-[#24344e] hover:to-[#1a2230]">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 pb-3">
        <div className="min-w-0 space-y-2">
          <h2 className="text-base font-bold text-[#d7dee8] origin-left transition-all duration-200 group-hover:scale-[1.06] group-hover:text-white">Lets see how your videos are doing</h2>
          {data?.connected ? (
            data?.syncing ? <p className="text-sm text-[#8b95a7]">Syncing…</p> : null
          ) : (
            <p className="text-sm text-[#c5cedb]">No channel connected. Use Connect YouTube at the top of the page.</p>
          )}
        </div>
        <div className="flex items-center justify-start sm:justify-end gap-3 shrink-0 flex-wrap">
          {showMetrics && onContentFormat ? (
            <ContentFormatSwitcher value={contentFormat} onChange={onContentFormat} spaceBeforeLast />
          ) : null}
          <Link to="/channel" className="ml-3 inline-flex items-center gap-1 text-xs font-semibold text-[#34d399] hover:text-white">
            More <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </div>

      {data?.connected && !data.scopes_ok ? (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <p className="text-sm text-amber-100">Reconnect YouTube to see your ratings.</p>
          <button type="button" onClick={connect} disabled={connectBusy} className="px-3 py-1.5 rounded-full bg-amber-300 text-[#1c1404] text-xs font-bold">
            {connectBusy ? 'Opening…' : 'Reconnect'}
          </button>
        </div>
      ) : null}

      {loading ? (
        <div className="h-14 flex items-center gap-2 text-sm text-[#9aa6b8]" role="status">
          <Loader2 className="w-4 h-4 animate-spin text-[#34d399]" />
          Loading…
        </div>
      ) : showMetrics ? (
        <div className="flex flex-col lg:flex-row lg:items-stretch gap-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:flex-1 lg:min-w-0">
            {stats.map(([label, period, value]) => (
              <div key={label} className="rounded-xl border-0 bg-white/[0.05] px-4 py-3 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.12em] text-[#7d8799]">{label}</p>
                <p className="text-[10px] text-[#5c6778] mt-0.5">{period}</p>
                <p className="text-lg font-semibold text-white leading-tight mt-2">{value}</p>
                {label === 'Fans' && gained != null ? (
                  <p className="text-[10px] text-[#9aa6b8] mt-1">{subscriberChange(gained)}</p>
                ) : null}
              </div>
            ))}
          </div>
          {(top?.video_id || latest?.video_id) ? (
            <div className="flex flex-col sm:flex-row gap-3 lg:shrink-0">
              {top?.video_id ? videoCard(
                top,
                'Best',
                `${formatCount(top.views_lifetime)} views`,
                `/channel/videos/${top.video_id}`,
              ) : null}
              {latest?.video_id ? videoCard(
                latest,
                'New',
                `${formatCount(latest.views)} views`,
                `/channel/videos/${latest.video_id}`,
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export function ChannelPerformanceBand() {
  const [preset, setPreset] = useState('28d');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [contentFormat, setContentFormat] = useState('all');
  const { data, loading, refreshing, connectBusy, refresh, connect } = useChannelOverview(
    preset,
    start,
    end,
    '/channel',
    contentFormat,
  );
  const totals = data?.totals || {};
  const showMetrics = Boolean(data?.connected && data?.scopes_ok);
  const rangeLabel = rangeCaption(preset, start, end);
  const allTimeSubs = data?.lifetime?.subscribers ?? data?.subscriber_count;
  const sideStats = [
    ['Watch time', formatWatch(totals.watch_minutes), rangeLabel],
    ['Avg view', formatDuration(totals.average_view_duration_seconds), rangeLabel],
    ['Impressions', formatCount(totals.impressions), rangeLabel],
    ['CTR', formatCtr(totals.ctr), rangeLabel],
    [
      'Subscribers',
      allTimeSubs == null ? '—' : formatCount(allTimeSubs),
      `All time · ${signedCount(totals.net_subscribers)} ${rangeLabel}`,
    ],
  ];

  const onRange = (next) => {
    setPreset(next.preset);
    setStart(next.start || '');
    setEnd(next.end || '');
  };

  return (
    <section id="channel-band" className="space-y-5" data-testid="channel-analytics">
      <header className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold tracking-tight text-white">Analytics</h1>
          <p className="mt-1 text-sm text-[#8b95a7] truncate">
            {data?.connected ? (data.channel_title || 'Your channel') : 'No channel connected'}
            {data?.connected && data?.syncing ? ' · Syncing' : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          disabled={refreshing || !data?.connected}
          title="Refresh"
          className="mt-1 h-10 w-10 shrink-0 rounded-full border border-white/10 bg-white/[0.03] text-white inline-flex items-center justify-center disabled:opacity-40 hover:bg-white/[0.06]"
        >
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        </button>
      </header>

      <ChannelSectionNav />

      {loading ? (
        <div className="h-14 flex items-center gap-2 text-sm text-[#9aa6b8]" role="status">
          <Loader2 className="w-4 h-4 animate-spin text-[#34d399]" />
          Loading this view…
        </div>
      ) : null}

      {data && !data.connected && (
        <div className="rounded-[28px] bg-gradient-to-br from-[#16352f] to-[#12151d] px-6 py-8 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1">
            <p className="text-lg font-semibold text-white">Connect YouTube</p>
            <p className="text-sm text-[#b7c2d0] mt-1">Views, watch time, and your strongest videos show up here.</p>
          </div>
          <button
            type="button"
            onClick={connect}
            disabled={connectBusy}
            className="px-5 py-2.5 rounded-full bg-[#34d399] text-[#06211b] text-sm font-bold"
          >
            {connectBusy ? 'Opening Google…' : 'Connect YouTube'}
          </button>
        </div>
      )}

      {data?.connected && !data.scopes_ok && (
        <div className="rounded-[28px] border border-amber-300/20 bg-amber-300/[0.06] px-6 py-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <p className="text-sm text-amber-50 flex-1">
            Reconnect YouTube so Twinnee can read analytics for this channel.
          </p>
          <button
            type="button"
            onClick={connect}
            disabled={connectBusy}
            className="px-5 py-2.5 rounded-full bg-amber-300 text-[#1c1404] text-sm font-bold"
          >
            {connectBusy ? 'Opening Google…' : 'Reconnect'}
          </button>
        </div>
      )}

      <div className="min-w-0 space-y-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <RangeSwitcher preset={preset} start={start} end={end} onChange={onRange} />
            <ContentFormatSwitcher value={contentFormat} onChange={setContentFormat} />
          </div>

          <div className="grid items-start gap-5 lg:grid-cols-2">
          <div className="min-w-0 space-y-5">
          {showMetrics && !loading ? (
            <div className="rounded-[24px] bg-gradient-to-br from-[#17352e] via-[#12181f] to-[#10141c] px-5 py-5">
              <h2 className="text-base font-semibold tracking-tight text-white">Analytics</h2>
              <p className="mt-3 text-sm text-[#9dbeb4]">
                {formatViewLabel(contentFormat)} · {rangeLabel}
                {typeof data?.video_count === 'number' ? ` · ${data.video_count} video${data.video_count === 1 ? '' : 's'}` : ''}
              </p>
              <p className="mt-1 text-4xl font-semibold tracking-tight text-white">
                {formatCount(totals.views)}
              </p>
              <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-4">
                {sideStats.map(([label, value, note]) => (
                  <div key={label}>
                    <p className="text-[11px] uppercase tracking-wide text-[#7f8b9c]">{label}</p>
                    <p className="mt-1 text-lg font-semibold text-white">{value}</p>
                    {note ? <p className="mt-1 text-[11px] text-[#8fb3a8]">{note}</p> : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {showMetrics && !loading ? (
            <div>
              <p className="text-sm font-medium text-white mb-3">Views over time · {rangeLabel}</p>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={data?.series || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="channelViewsFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#34d399" stopOpacity={0.38} />
                        <stop offset="100%" stopColor="#34d399" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <XAxis
                      dataKey="date"
                      tickFormatter={(value) => String(value || '').slice(5)}
                      tick={{ fill: '#7d8799', fontSize: 11 }}
                      tickLine={false}
                      axisLine={false}
                      minTickGap={28}
                    />
                    <YAxis tick={{ fill: '#7d8799', fontSize: 11 }} tickLine={false} axisLine={false} width={36} />
                    <Tooltip
                      contentStyle={{ background: '#12151d', border: '1px solid #243044', borderRadius: 14, fontSize: 12 }}
                      labelStyle={{ color: '#d5dbe6' }}
                    />
                    <Area type="monotone" dataKey="views" stroke="#34d399" strokeWidth={2.5} fill="url(#channelViewsFill)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : null}

          </div>
          <ChannelTrendsNav
            preset={preset}
            start={start}
            end={end}
            ready={Boolean(data)}
            connected={Boolean(data?.connected)}
            scopesOk={Boolean(data?.scopes_ok)}
          />
          </div>
      </div>

      {showMetrics && !loading ? (
        <div className="grid lg:grid-cols-2 gap-5">
          <VideoList title="Strongest" videos={data?.best_videos || []} />
          <VideoList title="Quietest" videos={data?.weakest_videos || []} />
        </div>
      ) : null}

      {showMetrics && !loading ? (
        <VideoList
          title={contentFormat === 'shorts' ? 'All Shorts' : contentFormat === 'videos' ? 'All YouTube videos' : 'All videos'}
          videos={data?.videos || []}
          scroll
        />
      ) : null}
    </section>
  );
}

function VideoList({ title, videos, scroll = false }) {
  const navigate = useNavigate();
  return (
    <div>
      <h2 className="text-sm font-semibold text-white mb-3">{title}</h2>
      {videos.length === 0 ? (
        <p className="text-sm text-[#6d7788]">Nothing in this view yet.</p>
      ) : (
        <ul className={`space-y-1 ${scroll ? 'max-h-[28rem] overflow-y-auto pr-1' : ''}`}>
          {videos.map((video, index) => (
            <li key={video.video_id} className="flex items-center gap-3 rounded-2xl px-2 py-2.5 hover:bg-white/[0.04]">
              <button
                type="button"
                onClick={() => navigate(`/channel/videos/${video.video_id}`)}
                className="min-w-0 flex-1 flex items-center gap-3 text-left"
              >
                <span className="w-6 text-sm font-medium text-[#5c6778]">{index + 1}</span>
                <VideoThumb video={video} />
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="block min-w-0 text-sm text-white truncate">{video.title || video.video_id}</span>
                    <span className="shrink-0 rounded-full border border-white/10 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-[#8b95a7]">
                      {video.is_short ? 'Short' : 'Video'}
                    </span>
                  </span>
                  <span className="block text-[11px] text-[#7d8799] mt-0.5">
                    {formatCount(Number(video.views_lifetime || 0))} views
                  </span>
                </span>
              </button>
              <SeoScoreRing
                score={video.seo_score}
                size={64}
                onImprove={() => navigate(`/channel/videos/${video.video_id}?improve=1`)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
